import React, { useState, useEffect, useCallback } from 'react';
import { Card, message, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/vi'; // Import locale tiếng Việt
import TransactionService from '../service/transaction-service/Transaction.service';
import useEmployee from '../hook/useEmployee';
import { useIsAdmin } from '../hook/useRole';
import {
  WithdrawalHeader,
  WithdrawalStatistics,
  WithdrawalTable,
  formatCurrency,
  formatCardNumber,
  TransactionData,
} from '../component/withdrawal-check';

const { Option } = Select;

// Cấu hình dayjs sử dụng locale tiếng Việt
dayjs.locale('vi');

const WithdrawalCheck: React.FC = () => {
  // All transactions data (toàn bộ dữ liệu trong tháng)
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  // Filtered transactions (sau khi áp dụng bộ lọc)
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [currentFilters, setCurrentFilters] = useState<any>({});

  const { employee } = useEmployee();
  const employeeLoading = false; // useEmployee doesn't return loading state
  const isAdmin = useIsAdmin();

  // Optimistic update function
  const handleOptimisticUpdate = useCallback(
    (transactionId: number, newStatus: string) => {
      setAllTransactions((prev) =>
        prev.map((transaction) =>
          transaction.id === transactionId
            ? { ...transaction, status: newStatus }
            : transaction
        )
      );

      setFilteredTransactions((prev) =>
        prev.map((transaction) =>
          transaction.id === transactionId
            ? { ...transaction, status: newStatus }
            : transaction
        )
      );
    },
    []
  );

  // Load all transactions for the month
  const loadTransactions = async () => {
    if (employeeLoading || (!isAdmin && !employee?.id)) return;

    try {
      setLoading(true);
      let transactions = [];

      if (isAdmin) {
        // Admin: Lấy tất cả giao dịch trong tháng
        transactions =
          await TransactionService.getAllWithdrawalTransactionsByMonth(
            selectedYear,
            selectedMonth
          );
      } else {
        // Employee: Lấy giao dịch của employee trong tháng
        transactions =
          await TransactionService.getAllWithdrawalTransactionsByMonthAndEmployee(
            selectedYear,
            selectedMonth,
            employee!.id
          );
      }

      console.log(`Loaded ${transactions.length} transactions`);

      // Cập nhật state một cách atomic
      setAllTransactions(transactions);

      // Apply current filters to new data
      applyFilters(transactions, currentFilters);
    } catch (error: any) {
      console.error('Error loading transactions:', error);
      message.error('Không thể tải danh sách giao dịch');
      setAllTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to transactions
  const applyFilters = (transactions: any[], filters: any) => {
    let filtered = [...transactions];

    // Apply filters
    if (filters.transactionDate && filters.transactionDate.length > 0) {
      filtered = filtered.filter((t) => {
        const transactionDate = dayjs(t.transactionDate);
        const dayOfMonth = transactionDate.date();
        return filters.transactionDate.includes(dayOfMonth);
      });
    }

    if (filters.creditCard) {
      filtered = filtered.filter((t) => {
        const cardNumber = t.creditCard?.cardNumber || t.cardNumber || '';
        const cardHolderName =
          t.creditCard?.cardHolderName || t.cardHolderName || '';
        const lastFourDigits = cardNumber.slice(-4);
        const searchTerm = filters.creditCard.toLowerCase();

        return (
          lastFourDigits.toLowerCase().includes(searchTerm) ||
          cardHolderName.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (filters.employee && filters.employee.length > 0) {
      filtered = filtered.filter((t) =>
        filters.employee.includes(t.employee?.name)
      );
    }

    if (filters.posDeviceCode && filters.posDeviceCode.length > 0) {
      filtered = filtered.filter((t) =>
        filters.posDeviceCode.includes(
          t.posDevice?.deviceCode || t.posDeviceCode
        )
      );
    }

    if (filters.amount) {
      filtered = filtered.filter((t) =>
        t.amount?.toString().includes(filters.amount)
      );
    }

    if (filters.feeApplied) {
      filtered = filtered.filter((t) =>
        t.feeApplied?.toString().includes(filters.feeApplied)
      );
    }

    if (filters.balance) {
      filtered = filtered.filter((t) =>
        t.balance?.toString().includes(filters.balance)
      );
    }

    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter((t) => filters.status.includes(t.status));
    }

    console.log(
      `Applied filters: ${filtered.length} transactions after filtering`
    );
    setFilteredTransactions(filtered);
  };

  // Handle filter changes from table
  const handleFilterChange = (filters: any) => {
    setCurrentFilters(filters);
    applyFilters(allTransactions, filters);
  };

  // Load transactions when month/year changes
  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, isAdmin, employee?.id, employeeLoading]);

  // Calculate statistics based on filtered data
  const calculateStatistics = () => {
    if (filteredTransactions.length === 0) {
      return {
        totalAmount: 0,
        totalBalance: 0,
        totalFee: 0,
        completedCount: 0,
        pendingCount: 0,
      };
    }

    const totalAmount = filteredTransactions.reduce(
      (sum, t) => sum + (t.amount || 0),
      0
    );
    const totalBalance = filteredTransactions.reduce(
      (sum, t) => sum + (t.balance || 0),
      0
    );
    const totalFee = totalAmount - totalBalance;
    const completedCount = filteredTransactions.filter(
      (t) => t.status === 'COMPLETED'
    ).length;
    const pendingCount = filteredTransactions.filter(
      (t) => t.status === 'PENDING'
    ).length;

    return {
      totalAmount,
      totalBalance,
      totalFee,
      completedCount,
      pendingCount,
    };
  };

  return (
    <div className="p-6">
      <Card className="mb-6">
        <WithdrawalHeader loading={loading} onRefresh={loadTransactions} />

        {/* Month/Year Selection */}
        <div className="flex gap-4 mt-4">
          <Select
            value={selectedYear}
            onChange={setSelectedYear}
            style={{ width: 120 }}
          >
            {Array.from({ length: 3 }, (_, i) => dayjs().year() - i).map(
              (year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              )
            )}
          </Select>
          <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 120 }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <Option key={month} value={month}>
                Tháng {month}
              </Option>
            ))}
          </Select>
        </div>
      </Card>

      <Card className="mb-6">
        <WithdrawalStatistics
          transactions={filteredTransactions}
          formatCurrency={formatCurrency}
        />
      </Card>

      <Card>
        <WithdrawalTable
          // Pass filtered data and let Antd Table handle pagination
          transactions={filteredTransactions}
          loading={loading}
          onRefresh={loadTransactions}
          onFilterChange={handleFilterChange}
          formatCurrency={formatCurrency}
          formatCardNumber={formatCardNumber}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          isAdmin={isAdmin}
          employeeId={employee?.id}
          onOptimisticUpdate={handleOptimisticUpdate}
        />
      </Card>
    </div>
  );
};

export default WithdrawalCheck;
